import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface GetMobileBySerialResponse {
  mobile?: string;
  phone?: string;
  numero?: string;
  celular?: string;
}

/** Error en respuesta OTP (generate o validate). */
export interface OtpErrorBody {
  code?: string;
  message?: string;
}

/** Respuesta POST /api/otp/generate (email). */
export interface OtpGenerateResponse {
  successful: boolean;
  requestId: string | null;
  transactionId: string | null;
  error: OtpErrorBody | null;
}

/** Respuesta POST /api/otp/validate. */
export interface OtpValidateResponse {
  successful: boolean;
  requestId: string | null;
  transactionId: string | null;
  error: OtpErrorBody | null;
}

/**
 * Servicio para flujo Restablecer PIN: obtener móvil por SERIALNUMBER, generar OTP (email/celular) y validar OTP.
 */
@Injectable({
  providedIn: 'root'
})
export class ResetPinOtpService {
  private get mobileBySerialUrl(): string {
    return (environment as { resetPinGetMobileBySerialUrl?: string }).resetPinGetMobileBySerialUrl || '';
  }

  private get otpBaseUrl(): string {
    const base = (environment as { localAgentBaseUrl?: string }).localAgentBaseUrl || '';
    return base.replace(/\/$/, '');
  }

  constructor(private http: HttpClient) {}

  /**
   * Genera y envía OTP por correo.
   * POST {otpBaseUrl}/api/otp/generate con { toEmail }.
   */
  generateOtpEmail(toEmail: string): Observable<OtpGenerateResponse> {
    const url = `${this.otpBaseUrl}/api/otp/generate`;
    return this.http.post<OtpGenerateResponse>(url, { toEmail: (toEmail ?? '').trim().toLowerCase() }).pipe(
      map(res => {
        if (!res?.successful && res?.error?.message) {
          throw new Error(res.error.message);
        }
        return res;
      }),
      catchError(err => {
        const msg = err?.message ?? err?.error?.error?.message ?? err?.error?.message ?? 'Error al generar OTP';
        return throwError(() => new Error(msg));
      })
    );
  }

  /**
   * Valida el código OTP ingresado por el usuario.
   * POST {otpBaseUrl}/api/otp/validate con { transactionId, codeOtp }.
   */
  validateOtp(transactionId: string, codeOtp: string): Observable<OtpValidateResponse> {
    const url = `${this.otpBaseUrl}/api/otp/validate`;
    return this.http.post<OtpValidateResponse>(url, {
      transactionId: transactionId?.trim(),
      codeOtp: String(codeOtp ?? '').trim()
    }).pipe(
      map(res => {
        if (!res?.successful && res?.error?.message) {
          throw new Error(res.error.message);
        }
        return res;
      }),
      catchError(err => {
        const msg = err?.message ?? err?.error?.error?.message ?? err?.error?.message ?? 'Error al validar OTP';
        return throwError(() => new Error(msg));
      })
    );
  }

  /**
   * Obtiene el número móvil asociado al certificado por SERIALNUMBER del subject.
   * Si resetPinGetMobileBySerialUrl no está configurada, devuelve error (Observable).
   */
  getMobileByCertificateSerial(serialNumber: string): Observable<string> {
    const url = this.mobileBySerialUrl?.trim();
    if (!url || !serialNumber?.trim()) {
      return throwError(() => new Error('Servicio de consulta de móvil no configurado o serial no indicado'));
    }
    const params = { serialNumber: serialNumber.trim() };
    return this.http.get<GetMobileBySerialResponse>(url, { params }).pipe(
      map(res => {
        const mobile = res?.mobile ?? res?.phone ?? res?.numero ?? res?.celular;
        if (mobile && typeof mobile === 'string') return mobile.trim();
        throw new Error('No se encontró número móvil para el certificado');
      }),
      catchError(err => {
        const msg = err?.error?.message ?? err?.error?.errorMessage ?? err?.message ?? 'Error al obtener el número móvil';
        return throwError(() => new Error(msg));
      })
    );
  }
}
